import { mergeAttributes, Node } from "@tiptap/core";

/**
 * TipTap atom node that preserves source-map-link citation badges
 * through the edit/save cycle in report sections.
 *
 * These inline anchors carry data-section-id / data-page-id /
 * data-bates-range attributes that link report text to source documents.
 * Without this extension, TipTap would strip the <a> tags on parse and
 * the citation metadata would be permanently lost on save.
 */
export const SourceMapLink = Node.create({
  name: "sourceMapLink",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      sectionId: { default: null },
      pageId: { default: null },
      firstPageId: { default: null },
      batesRange: { default: null },
      refNumber: { default: null },
      label: { default: "" },
      url: { default: "#" },
      reference: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "a",
        priority: 51,
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          if (!el.classList.contains("source-map-link")) return false;
          return {
            sectionId: el.getAttribute("data-section-id"),
            pageId: el.getAttribute("data-page-id"),
            firstPageId: el.getAttribute("data-first-page-id"),
            batesRange: el.getAttribute("data-bates-range"),
            refNumber: el.getAttribute("data-ref-number"),
            label: el.textContent || "",
            url: el.getAttribute("href") || "#",
            reference: el.getAttribute("data-page-id")
              ? {
                  referenceType: "page",
                  pageId: el.getAttribute("data-page-id"),
                  batesNumber: el.getAttribute("data-bates-range") ?? undefined,
                }
              : null,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const label: string = node.attrs.label ?? "";
    const reference =
      typeof node.attrs.reference === "object" && node.attrs.reference !== null
        ? (node.attrs.reference as Record<string, unknown>)
        : null;
    const attrs: Record<string, string> = {
      class: "source-map-link",
      href: node.attrs.url || "#",
      role: "button",
    };
    const sectionId = node.attrs.sectionId ?? reference?.sectionId;
    const pageId = node.attrs.pageId ?? reference?.pageId;
    const batesRange =
      node.attrs.batesRange ?? reference?.batesRange ?? reference?.batesNumber;
    const refNumber = node.attrs.refNumber ?? reference?.displayNumber;
    if (sectionId) {
      attrs["data-section-id"] = String(sectionId);
    }
    if (pageId) {
      attrs["data-page-id"] = String(pageId);
    }
    if (node.attrs.firstPageId) {
      attrs["data-first-page-id"] = node.attrs.firstPageId;
    }
    if (batesRange) {
      attrs["data-bates-range"] = String(batesRange);
    }
    if (refNumber) {
      attrs["data-ref-number"] = String(refNumber);
    }
    if (label.length > 20) {
      attrs["title"] = label;
    }
    return ["a", mergeAttributes(attrs), label];
  },
});
